import { useState, useEffect, useRef } from 'react'
import { useDatasetFiles } from '@/api/hooks/useDatasetFiles'
import { useKnowledgeStore } from '@/stores/knowledgeStore'
import { useWorkspaceStore } from '@/stores/workspaceStore'
import { Skeleton } from '@you/ui'
import FilePreview from '@/components/common/FilePreview'
import { DatasetFile } from '@/api/types/datasetFiles.type'
import { UploadFile as UploadFileType } from '@/components/common/UploadFile'

interface DatasetPreviewProps {
  fileList: DatasetFile[] | UploadFileType[]
  serverFileList?: DatasetFile[]
}

const DatasetPreviewText: React.FC<DatasetPreviewProps> = ({
  fileList,
  serverFileList = [],
}) => {
  const { currentKnowledge, currentDataset } = useKnowledgeStore()
  const { currentWorkspace } = useWorkspaceStore()
  const { getFilePreview } = useDatasetFiles({
    datasetId: currentDataset?.id || '',
  })

  // 当前选中的文档
  const [selectedDocumentId, setSelectedDocumentId] = useState<string>(
    fileList[0]?.id || ''
  )
  // 分段预览数据
  const [segments, setSegments] = useState<string[]>([])
  // 加载状态
  const [isLoading, setIsLoading] = useState(false)
  // 防止重复请求的 ref
  const requestRef = useRef<string | null>(null)

  // 获取分段预览数据
  const fetchSegments = async (fileId: string) => {
    // 检查是否是本地上传的文件
    const localFile = fileList.find(
      (file) => file.id === fileId && 'file' in file
    )

    // 对于本地上传的文件，使用 serverId 请求文件流
    if (localFile && 'file' in localFile && localFile.serverId) {
      if (!currentDataset?.id || !currentKnowledge?.id || !currentWorkspace?.id)
        return

      try {
        setIsLoading(true)
        const result = await getFilePreview({
          datasetFileId: localFile.serverId,
          datasetId: currentDataset.id,
          knowledgeBaseId: currentKnowledge.id,
          workspaceId: currentWorkspace.id,
        })
        setSegments(result.segments || [])
      } catch (error) {
        console.error('获取分段预览失败:', error)
        setSegments([])
      } finally {
        setIsLoading(false)
      }
      return
    }

    // 对于服务器文件，从API获取分段
    if (
      !fileId ||
      !currentDataset?.id ||
      !currentKnowledge?.id ||
      !currentWorkspace?.id
    )
      return

    try {
      setIsLoading(true)
      const result = await getFilePreview({
        datasetFileId: fileId,
        datasetId: currentDataset.id,
        knowledgeBaseId: currentKnowledge.id,
        workspaceId: currentWorkspace.id,
      })
      setSegments(result.segments || [])
    } catch (error) {
      console.error('获取分段预览失败:', error)
      setSegments([])
    } finally {
      setIsLoading(false)
    }
  }

  // 当选中文档变化时，获取分段预览
  useEffect(() => {
    console.log('selectedDocumentId', selectedDocumentId)
    if (selectedDocumentId && requestRef.current !== selectedDocumentId) {
      requestRef.current = selectedDocumentId
      fetchSegments(selectedDocumentId)
    }
  }, [selectedDocumentId])

  // 当 fileList 变化时，更新选中的文档
  useEffect(() => {
    if (fileList.length > 0 && !selectedDocumentId) {
      setSelectedDocumentId(fileList[0].id)
    }
  }, [fileList, selectedDocumentId])

  // 渲染标题
  const renderTitle = (title: string) => {
    return (
      <div className="mb-2 px-2">
        <h3 className="text-base font-bold text-[#655093]">{title}</h3>
      </div>
    )
  }

  // 渲染文档列表项
  const renderDocumentItem = (document: DatasetFile | UploadFileType) => {
    const isSelected = document.id === selectedDocumentId
    const fileName =
      'fileName' in document ? document.fileName : document.file.name

    return (
      <div
        key={document.id}
        className={`
          h-12 px-2 rounded-lg flex items-center cursor-pointer transition-colors
          ${
            isSelected
              ? 'bg-[#F9FAFF] text-[#333333]'
              : 'text-[#333333] hover:bg-gray-50'
          }
        `}
        onClick={() => setSelectedDocumentId(document.id)}
      >
        <span className="text-base truncate">{fileName}</span>
      </div>
    )
  }

  // 渲染左侧文档列表
  const renderDocumentList = () => (
    <div className="flex flex-col h-full">
      {/* 标题 */}
      {renderTitle('文档列表')}

      {/* 内容区域 */}
      <div className="flex-1 bg-white rounded-lg p-2 overflow-y-auto">
        <div className="space-y-2">{fileList.map(renderDocumentItem)}</div>
      </div>
    </div>
  )

  // 渲染中间原文件预览
  const renderOriginalPreview = () => {
    const selectedFile = fileList.find((doc) => doc.id === selectedDocumentId)

    // 如果是本地上传文件，根据 serverId 找到对应的服务器文件
    let fileUrl = ''
    if (selectedFile && 'file' in selectedFile && selectedFile.serverId) {
      const serverFile = serverFileList.find(
        (file) => file.id === selectedFile.serverId
      )
      fileUrl = serverFile?.url || ''
    } else if (selectedFile && 'url' in selectedFile) {
      fileUrl = selectedFile.url || ''
    }

    return (
      <div className="flex flex-col h-full">
        {/* 标题 */}
        {renderTitle('原始文档')}

        {/* 内容区域 */}
        <div className="flex-1 bg-white rounded-lg overflow-hidden">
          {fileUrl ? (
            <FilePreview fileUrl={fileUrl} />
          ) : (
            <div className="flex items-center justify-center h-full text-gray-500">
              <div className="text-center">
                <div className="text-lg mb-2">📄</div>
                <div>原始文档预览</div>
                <div className="text-sm mt-1">
                  {selectedFile &&
                  'file' in selectedFile &&
                  !selectedFile.serverId
                    ? '文件尚未上传到服务器'
                    : '无法获取文件预览'}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    )
  }

  // 渲染右侧分段预览
  const renderSegmentPreview = () => (
    <div className="flex flex-col h-full">
      {/* 标题 */}
      {renderTitle('分段预览')}

      {/* 内容区域 */}
      <div className="flex-1 bg-white rounded-lg p-4 overflow-y-auto">
        {isLoading ? (
          <div className="space-y-4">
            <Skeleton className="h-20 w-full" />
            <Skeleton className="h-20 w-full" />
            <Skeleton className="h-20 w-full" />
          </div>
        ) : segments.length > 0 ? (
          <div className="space-y-4">
            {segments.map((segment, index) => (
              <div
                key={index}
                className="p-4 rounded-lg bg-gray-50 hover:bg-gray-100 transition-colors"
              >
                <div className="text-sm text-gray-400 mb-1">
                  段落 {index + 1} / {segments.length}
                </div>
                <div className="text-base text-gray-700 whitespace-pre-wrap">
                  {segment}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center text-gray-500 py-8">
            <div className="text-lg mb-2">📋</div>
            <div>分段内容预览</div>
            <div className="text-sm mt-1">暂无分段数据</div>
          </div>
        )}
      </div>
    </div>
  )

  return (
    <div className="h-full w-full bg-[#F9FAFF] rounded-lg p-2">
      {/* 三栏布局 */}
      <div className="flex gap-2 h-full">
        {/* 左侧：文档列表 */}
        <div className="w-64 flex-shrink-0">{renderDocumentList()}</div>

        {/* 中间：原文件预览 */}
        <div className="flex-1">{renderOriginalPreview()}</div>

        {/* 右侧：分段预览 */}
        <div className="w-80 flex-1">{renderSegmentPreview()}</div>
      </div>
    </div>
  )
}

export default DatasetPreviewText
